import { useEffect } from 'react'

export function GridRunnerBackground() {
  // Grid settings
  const gridSize = 40 // Same as old code

  useEffect(() => {
    const runner = document.getElementById('grid-runner')
    if (!runner) return
    const runnerElement = runner as HTMLElement

    // Use clientWidth to account for scrollbar width
    let currentX = document.body.clientWidth / 2
    let currentY = -40 // Start outside viewport

    // Direction: 0 = Down, 1 = Right, 2 = Left
    let direction = 0

    let timeoutId: number

    function updatePosition() {
      document.documentElement.style.setProperty('--runner-x', `${currentX}px`)
      document.documentElement.style.setProperty('--runner-y', `${currentY}px`)
    }

    // Set initial position
    updatePosition()

    function gameLoop() {
      const maxX = document.body.clientWidth

      // Reset logic: If too far down, reset
      if (currentY > document.body.scrollHeight + 100 || currentY > 1200) {
        currentY = -40
        currentX = document.body.clientWidth / 2
        direction = 0

        // Disable transition for instant reset
        runnerElement.style.transition = 'none'
        updatePosition()

        // Re-enable transition and restart after short pause
        setTimeout(() => {
          runnerElement.style.transition = 'transform 0.6s linear'
          gameLoop()
        }, 100)
        return
      }

      // --- DIRECTION LOGIC ---
      const r = Math.random()

      if (direction === 0) { // Currently moving DOWN
        // 20% chance to turn (10% Left, 10% Right)
        if (r < 0.1) direction = 1 // Go Right
        else if (r < 0.2) direction = 2 // Go Left
        // else: keep going down (80% chance)
      } else { // Currently moving SIDEWAYS (Right or Left)
        // 80% chance to go DOWN again
        if (r < 0.8) direction = 0 // Go Down
        // else: keep going sideways (20% chance)
      }

      // --- BOUNDARY CHECKS ---
      // Don't let it run off screen
      if (currentX <= gridSize * 2) direction = 1 // Must go right
      if (currentX >= maxX - (gridSize * 2)) direction = 2 // Must go left

      // --- EXECUTE MOVEMENT ---
      if (direction === 0) currentY += gridSize
      else if (direction === 1) currentX += gridSize
      else if (direction === 2) currentX -= gridSize

      updatePosition()

      // Loop speed: 600ms (synced with CSS transition)
      timeoutId = window.setTimeout(() => {
        gameLoop()
      }, 600)
    }

    // Start with delay
    timeoutId = window.setTimeout(gameLoop, 100)

    // Handle resize
    const handleResize = () => {
      runnerElement.style.transition = 'none'
      currentX = document.body.clientWidth / 2
      updatePosition()
      setTimeout(() => {
        runnerElement.style.transition = 'transform 0.6s linear'
      }, 50)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
    }
  }, [])

  return (
    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none h-screen select-none">
      <style>{`
        :root {
          --grid-size: 40px;
          --grid-color: rgba(15, 23, 42, 0.04);
          --runner-x: 50%;
          --runner-y: 0px;
        }

        /* Layer 1: Base Grid (static) */
        .bg-grid-base {
          background-size: var(--grid-size) var(--grid-size);
          /* Centered vertical line within the 40px tile */
          background-image:
            linear-gradient(to right, transparent 19.5px, var(--grid-color) 1px, transparent 20.5px),
            linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px);
          /* Centers the tile (so center of tile is at center of screen) */
          background-position: 50% 0;
        }

        /* Layer 2: Highlight Grid (revealed by mask) */
        .bg-grid-highlight {
          background-size: var(--grid-size) var(--grid-size);
          background-image:
            linear-gradient(to right, transparent 19.5px, #237CCA 1px, transparent 20.5px),
            linear-gradient(to bottom, #10b981 1px, transparent 1px);
          background-position: 50% 0;
          mask-image: radial-gradient(circle 100px at var(--runner-x) var(--runner-y), black, transparent 95%);
          -webkit-mask-image: radial-gradient(circle 100px at var(--runner-x) var(--runner-y), black, transparent 95%);
          opacity: 0.8;
          pointer-events: none;
        }

        /* The Runner (dot) */
        .grid-runner {
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 4px;
          background: #10b981;
          border-radius: 50%;
          /* Corrected Offsets: X -2px (aligns 4px dot with centered 1px line), Y -1.5px (aligns with top-edge 1px line) */
          transform: translate3d(calc(var(--runner-x) - 2px), calc(var(--runner-y) - 1.5px), 0);
          box-shadow: 0 0 12px 3px rgba(16, 185, 129, 0.6);
          z-index: 10;
          will-change: transform;
          /* CRITICAL: CSS Transition for smooth movement */
          transition: transform 0.6s linear;
        }
      `}</style>

      {/* 1. Base Grid */}
      <div className="absolute inset-0 bg-grid-base" />

      {/* 2. Highlight Grid (masked) */}
      <div className="absolute inset-0 bg-grid-highlight" />

      {/* 3. Runner Dot */}
      <div id="grid-runner" className="grid-runner" />
    </div>
  )
}
